/**
 * LoginRequiredModal — bloquea el flujo de pago para invitados.
 *
 * Uso:
 *   const [needLogin, setNeedLogin] = useState(false);
 *   ...
 *   if (!authUser) { setNeedLogin(true); return; }
 *   // ...continuar con el pago...
 *
 *   <LoginRequiredModal
 *     open={needLogin}
 *     onClose={() => setNeedLogin(false)}
 *     returnPath={location.pathname + location.search}
 *   />
 *
 * Pasa `returnPath` para que tras login el fan vuelva exactamente a la
 * pantalla donde iba a pagar.
 */
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { LogIn, UserPlus, X } from "lucide-react";

export default function LoginRequiredModal({ open, onClose, returnPath = "/" }) {
  const navigate = useNavigate();

  const goLogin = () => {
    onClose?.();
    navigate(`/login?returnTo=${encodeURIComponent(returnPath)}`);
  };

  const goRegister = () => {
    onClose?.();
    // Fan registration flow. Carries the same returnTo so the user lands back
    // on the payment screen after creating their account.
    navigate(`/register?returnTo=${encodeURIComponent(returnPath)}`);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          onClick={onClose}
          data-testid="login-required-modal"
        >
          <motion.div
            initial={{ y: 40, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 40, scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-card border-2 border-primary/40 rounded-2xl p-6 sm:p-7 shadow-2xl shadow-primary/10"
          >
            {/* Close icon */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
              title="Cerrar"
              data-testid="login-modal-close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Heading */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
                <LogIn className="w-5 h-5 text-primary" />
              </div>
              <h2 className="font-heading font-bold text-white text-xl uppercase tracking-tight">
                Inicia sesión<br />para continuar
              </h2>
            </div>

            {/* Body */}
            <p className="text-sm text-zinc-300 font-body leading-relaxed mb-6">
              Para comprar servicios a las modelos es necesario que inicies
              sesión o crees una cuenta. Así podremos asociar correctamente
              tus compras y mostrar los enlaces a la modelo.
            </p>

            {/* Actions */}
            <div className="space-y-2.5">
              <button
                onClick={goLogin}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary hover:bg-primary/90 text-black font-heading font-bold text-sm uppercase tracking-wide transition-colors"
                data-testid="login-modal-login-btn"
              >
                <LogIn className="w-4 h-4" />
                Iniciar sesión
              </button>
              <button
                onClick={goRegister}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white font-heading font-bold text-sm uppercase tracking-wide transition-colors"
                data-testid="login-modal-register-btn"
              >
                <UserPlus className="w-4 h-4" />
                Crear cuenta
              </button>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 text-xs text-zinc-500 hover:text-white font-body transition-colors"
                data-testid="login-modal-cancel-btn"
              >
                Cancelar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
